import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Grid,
  IconButton,
  Typography,
  Box,
  Autocomplete,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  Avatar,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import StoreIcon from "@mui/icons-material/Store";
import InventoryIcon from "@mui/icons-material/Inventory";
import ReceiptIcon from "@mui/icons-material/Receipt";
import NotesIcon from "@mui/icons-material/Notes";
import SaveIcon from "@mui/icons-material/Save";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import {
  useCreateEcommerce,
  useGetEcomerce,
  useUpdateMarketplace,
} from "../../../client/hooks/marketplace/marketplace.queries";
import { useQueryClient } from "@tanstack/react-query";
import CustomSnackbar from "../../components/Message.Component";
import FornecedorDialog from "../../components/ConfigComponents/fornecedores.dialog.component";
import TabelaPedido from "./TableOrder.component";
import TotaisCompra from "./TotaisCompra.component";
import DetailsPurchase from "../../Orders/components/DetailsPurchase.component";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";

// Lista de fornecedores mockados para o autocomplete
const fornecedoresMock = [
  {
    id: 1,
    name: "Distribuidora ABC Ltda",
    email: "compras@abcltda.com.br",
    cnpj: "12.345.678/0001-90",
    logo: "https://via.placeholder.com/40",
  },
  {
    id: 2,
    name: "Fornecedora XYZ S/A",
    email: "vendas@xyzsa.com.br",
    cnpj: "23.456.789/0001-01",
    logo: "https://via.placeholder.com/40",
  },
  {
    id: 3,
    name: "Indústria Nacional Ltda",
    email: "pedidos@indnacional.com.br",
    cnpj: "34.567.890/0001-12",
    logo: "https://via.placeholder.com/40",
  },
  {
    id: 4,
    name: "Importadora Global EIRELI",
    email: "comercial@importglobal.com.br",
    cnpj: "45.678.901/0001-23",
    logo: "https://via.placeholder.com/40",
  },
  {
    id: 5,
    name: "Atacadista Máxima S/A",
    email: "atacado@maxima.com.br",
    cnpj: "56.789.012/0001-34",
    logo: "https://via.placeholder.com/40",
  },
];

// Produtos mockados para o autocomplete na tabela de itens
export const produtosMock = [
  {
    id: 1,
    nome: "Notebook Dell Inspiron",
    codigo: "DELL001",
    preco: 3499.9,
    unidade: "UN",
  },
  {
    id: 2,
    nome: "Monitor LG 24 polegadas",
    codigo: "LG002",
    preco: 799.9,
    unidade: "UN",
  },
  {
    id: 3,
    nome: "Teclado sem fio Logitech",
    codigo: "LOG003",
    preco: 199.9,
    unidade: "UN",
  },
  {
    id: 4,
    nome: "Mouse Gamer Razer",
    codigo: "RAZ004",
    preco: 299.9,
    unidade: "UN",
  },
  {
    id: 5,
    nome: "Cadeira Escritório Ergonômica",
    codigo: "CAD005",
    preco: 899.9,
    unidade: "UN",
  },
  {
    id: 6,
    nome: "Mesa de Escritório",
    codigo: "MES006",
    preco: 599.9,
    unidade: "UN",
  },
  {
    id: 7,
    nome: "SSD Kingston 480GB",
    codigo: "KIN007",
    preco: 349.9,
    unidade: "UN",
  },
  {
    id: 8,
    nome: "HD Externo Seagate 1TB",
    codigo: "SEA008",
    preco: 399.9,
    unidade: "UN",
  },
  {
    id: 9,
    nome: "Impressora HP Multifuncional",
    codigo: "HP009",
    preco: 999.9,
    unidade: "UN",
  },
  {
    id: 10,
    nome: "Papel A4 (Resma 500 folhas)",
    codigo: "PAP010",
    preco: 29.9,
    unidade: "RM",
  },
];

export default function PurchaseOrderDialog({
  open,
  onClose,
  onSave,
  initialData,
  orderNumber,
}) {
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [tableItensOrder, setTableItensOrder] = useState({ itens: [] });
  const [fornecedor, setFornecedor] = useState(null);
  const [tempFornecedor, setTempFornecedor] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [performFornecedores, setPerformFornecedores] = useState(true);
  const [internObservation, setInternObservation] = useState("");
  const [externObservation, setExternObservation] = useState("");
  const {
    data: fornecedoresData,
    isLoading: isLoadingFornecedores,
    isError: isErrorFornecedores,
    error: errorFornecedores,
  } = useGetEcomerce(performFornecedores);
  const [performCreateOrder, setPerformCreateOrder] = useState(false);
  const [performUpdateOrder, setPerformUpdateOrder] = useState(false);
  const { data, isError, error, isLoading, refetch } = useCreateEcommerce(
    formData,
    performCreateOrder
  );

  const {
    data: dataUpdate,
    isError: isErrorUpdate,
    error: errorUpdate,
    isLoading: isLoadingUpdate,
    refetch: refetchUpdate,
  } = useUpdateMarketplace(formData, formData.id, performUpdateOrder);

  const [alert, setAlert] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Usar fornecedores mockados em vez de dados da API
  const [options, setOptions] = useState(fornecedoresMock);

  // Steps (etapas) do formulário
  const steps = [
    { label: "Fornecedor", icon: <StoreIcon /> },
    { label: "Itens", icon: <InventoryIcon /> },
    { label: "Detalhes", icon: <ReceiptIcon /> },
    { label: "Observações", icon: <NotesIcon /> },
  ];

  function resetQueryData(key) {
    queryClient.resetQueries([key]);
  }

  useEffect(() => {
    if (open) {
      setFormData({});
      setActiveStep(0);
      if (!performCreateOrder && !performUpdateOrder) {
        if (
          queryClient.getQueryState("ecommerce-create")?.isFeTching ||
          queryClient.getQueryState("ecommerce-update")?.isFeTching
        ) {
          resetQueryData("ecommerce-create");
          resetQueryData("ecommerce-update");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && initialData && initialData.id) {
      // Para edição, carregar os dados existentes
      setTableItensOrder({ itens: initialData.itens || [] });
      setFornecedor(
        fornecedoresMock.find((f) => f.name === initialData.contact_name) ||
          null
      );
      setInternObservation(initialData.note || "");
      setExternObservation(initialData.externObservation || "");
      setFormData(initialData.detalhesCompra || {});
    } else {
      // Para criação, limpar os dados
      setTableItensOrder({ itens: [] });
      setFornecedor(null);
      setInternObservation("");
      setExternObservation("");
      setFormData({});
    }
  }, [open, initialData]);

  // Funções para navegação entre etapas
  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const validateCurrentStep = () => {
    switch (activeStep) {
      case 0: // Fornecedor
        return !!fornecedor;
      case 1: // Itens
        return tableItensOrder.itens && tableItensOrder.itens.length > 0;
      case 2: // Detalhes
        return true; // Sem validação específica
      case 3: // Observações
        return true; // Opcionais
      default:
        return true;
    }
  };

  const validateFields = () => {
    // Validação final antes de salvar
    if (!fornecedor) {
      setErrorMsg("Selecione um fornecedor");
      setAlert(true);
      setActiveStep(0);
      return false;
    }

    if (!tableItensOrder.itens || tableItensOrder.itens.length === 0) {
      setErrorMsg("Adicione pelo menos um item ao pedido");
      setAlert(true);
      setActiveStep(1);
      return false;
    }

    return true;
  };

  function saveChanges() {
    if (!validateFields()) {
      return;
    }

    // Preparar dados para salvar
    const dadosPedido = {
      id: initialData.id
        ? initialData.id
        : Math.floor(Math.random() * 10000) + 1000,
      contact_name: fornecedor.name,
      email: fornecedor.email,
      date: new Date().toLocaleDateString("pt-BR"),
      note: internObservation,
      externObservation: externObservation,
      itens: tableItensOrder.itens,
      status: initialData.id ? initialData.status : "Aguardando aprovação",
      detalhesCompra: {
        ...formData,
        createdAt: new Date().toLocaleDateString("pt-BR"),
        expectedDate:
          formData.expectedDate ||
          new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toLocaleDateString(
            "pt-BR"
          ),
      },
    };

    // Simulando chamada à API
    setTimeout(() => {
      setSuccessMsg(
        initialData.id
          ? "Pedido atualizado com sucesso!"
          : "Pedido criado com sucesso!"
      );
      setAlert(true);
      onSave(dadosPedido);
    }, 800);
  }

  const handleAddFornecedor = (isEdit = false) => {
    if (isEdit) {
      setTempFornecedor(fornecedor || null);
    } else {
      setTempFornecedor(null);
    }
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
  };

  function loadMsg(data) {
    setOpenDialog(false);
    setAlert(true);
    setSuccessMsg("Fornecedor criado com sucesso");
    setPerformFornecedores(true);
    // Adicionar o novo fornecedor à lista de opções
    const novoFornecedor = {
      id: options.length + 1,
      name: data.name || "Novo Fornecedor",
      email: data.email || "email@fornecedor.com.br",
      cnpj: data.cnpj || "00.000.000/0000-00",
      logo: "https://via.placeholder.com/40",
    };
    setOptions([...options, novoFornecedor]);
    setFornecedor(novoFornecedor);
  }

  useEffect(() => {
    if (isError || isErrorUpdate || isErrorFornecedores) {
      const errorMessage =
        error?.response?.data?.msg ||
        error?.message ||
        errorUpdate?.response?.data?.msg ||
        errorUpdate?.message ||
        errorFornecedores?.response?.data?.msg ||
        errorFornecedores?.message ||
        "Ocorreu um erro inesperado.";
      setErrorMsg(errorMessage);
      setAlert(true);
    }

    if (data && data.id) {
      onSave(data);
    }

    if (dataUpdate && dataUpdate.user && dataUpdate.user.id) {
      setSuccessMsg("Pedido de compra atualizado com sucesso");
      setAlert(true);
    }
    setPerformCreateOrder(false);
    setPerformUpdateOrder(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isError,
    error,
    data,
    dataUpdate,
    errorUpdate,
    isErrorUpdate,
    fornecedoresData,
    isErrorFornecedores,
  ]);

  const resetForm = () => {
    setFormData({});
    setTableItensOrder({ itens: [] });
    setFornecedor(null);
    setInternObservation("");
    setExternObservation("");
    setActiveStep(0);
  };

  const handleClose = (reload = false) => {
    resetForm();
    onClose(reload);
  };

  // Renderiza o conteúdo com base na etapa atual
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ py: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Selecione o Fornecedor
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                bgcolor: "#f8f9fa",
                borderRadius: 2,
                border: "1px solid #e0e0e0",
              }}
            >
              <Autocomplete
                freeSolo
                options={options}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.name
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={fornecedor}
                onChange={(event, newValue) => {
                  setFornecedor(newValue);
                }}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Avatar
                      src={option.logo}
                      sx={{ mr: 2, width: 32, height: 32 }}
                    />
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        {option.email} • CNPJ: {option.cnpj}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    id="fornecedor"
                    name="fornecedor"
                    label="Selecione o fornecedor"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => handleAddFornecedor(false)}
                              title="Adicionar novo fornecedor"
                            >
                              <AddCircleOutlineIcon
                                style={{
                                  color: "#3498db",
                                  display: !fornecedor ? "block" : "none",
                                }}
                              />
                            </IconButton>
                          </InputAdornment>
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => handleAddFornecedor(true)}
                              title="Editar fornecedor"
                            >
                              <CreateOutlinedIcon
                                style={{
                                  color: "#3498db",
                                  display: !fornecedor ? "none" : "block",
                                }}
                              />
                            </IconButton>
                          </InputAdornment>
                        </>
                      ),
                    }}
                  />
                )}
              />

              {fornecedor && (
                <Box
                  sx={{
                    mt: 4,
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, color: "text.secondary" }}
                  >
                    Dados do Fornecedor Selecionado
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>Nome:</strong> {fornecedor.name}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Email:</strong> {fornecedor.email}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2">
                        <strong>CNPJ:</strong> {fornecedor.cnpj}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Código:</strong> {fornecedor.id}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ py: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Itens do Pedido
            </Typography>
            <TabelaPedido
              itensProps={tableItensOrder.itens || []}
              onValoresAtualizados={(novosItens) =>
                setTableItensOrder((prevData) => ({
                  ...prevData,
                  itens: novosItens,
                }))
              }
            />
          </Box>
        );

      case 2:
        return (
          <Box sx={{ py: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Detalhes e Valores
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Totais da Compra
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: "#f8f9fa",
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <TotaisCompra
                    itens={tableItensOrder.itens || []}
                    onTotaisAtualizados={(totaisAtualizados) => {
                      setFormData((prev) => ({
                        ...prev,
                        totais: totaisAtualizados,
                      }));
                    }}
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Informações do Pedido
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: "#f8f9fa",
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <DetailsPurchase
                    orderNumber={orderNumber}
                    onDetalhesAtualizados={(detalhesAtualizados) => {
                      setFormData((prev) => ({
                        ...prev,
                        detalhesCompra: detalhesAtualizados,
                      }));
                    }}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ py: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Observações
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Observação Interna
                </Typography>
                <TextField
                  multiline
                  fullWidth
                  rows={4}
                  placeholder="Observações visíveis apenas para sua empresa"
                  value={internObservation}
                  onChange={(e) => setInternObservation(e.target.value)}
                  sx={{ bgcolor: "#fff" }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Estas observações não serão visíveis para o fornecedor
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Observação Externa
                </Typography>
                <TextField
                  multiline
                  fullWidth
                  rows={4}
                  placeholder="Observações que serão enviadas ao fornecedor"
                  value={externObservation}
                  onChange={(e) => setExternObservation(e.target.value)}
                  sx={{ bgcolor: "#fff" }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Estas observações serão enviadas ao fornecedor junto com o
                  pedido
                </Typography>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <CustomSnackbar
        open={alert}
        onClose={() => setAlert(false)}
        severity={
          isError || isErrorUpdate || isErrorFornecedores ? "error" : "success"
        }
        message={
          isError || isErrorUpdate || isErrorFornecedores
            ? errorMsg
            : successMsg
        }
      />
      <Dialog
        open={open}
        fullScreen
        PaperProps={{
          sx: {
            borderTop: (theme) => `4px solid ${theme.palette.primary.main}`,
            borderRadius: "0 0 8px 8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #e0e0e0",
            padding: "16px 24px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ShoppingCartIcon sx={{ mr: 1, color: "#3498db" }} />
            <Typography variant="h6">
              {initialData.id
                ? "Editar Pedido #" + initialData.id
                : "Novo Pedido de Compra"}
            </Typography>
            {initialData.id && (
              <Chip
                label={initialData.status}
                size="small"
                sx={{
                  ml: 2,
                  bgcolor:
                    initialData.status === "Aprovado"
                      ? "#27ae60"
                      : initialData.status === "Aguardando aprovação"
                        ? "#f39c12"
                        : "#3498db",
                  color: "white",
                }}
              />
            )}
          </Box>
          <IconButton onClick={() => handleClose(false)} aria-label="fechar">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Box
          sx={{
            width: "100%",
            px: 3,
            py: 2,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Stepper activeStep={activeStep}>
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={({ active, completed }) => (
                    <Avatar
                      sx={{
                        bgcolor: active || completed ? "#3498db" : "#e0e0e0",
                        width: 32,
                        height: 32,
                      }}
                    >
                      {step.icon}
                    </Avatar>
                  )}
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <DialogContent>{renderStepContent(activeStep)}</DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
          <Box>
            {activeStep > 0 && (
              <Button
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
                variant="outlined"
                color="inherit"
              >
                Anterior
              </Button>
            )}
          </Box>
          <Box>
            <Button
              onClick={() => handleClose(false)}
              color="inherit"
              variant="text"
              sx={{ mr: 1 }}
            >
              Cancelar
            </Button>

            {activeStep < steps.length - 1 ? (
              <Button
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
                variant="contained"
                color="primary"
                disabled={!validateCurrentStep()}
              >
                Próximo
              </Button>
            ) : (
              <Button
                onClick={saveChanges}
                variant="contained"
                color="primary"
                disabled={isLoadingUpdate || isLoading}
                startIcon={<SaveIcon />}
              >
                {initialData.id ? "Atualizar Pedido" : "Criar Pedido"}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <FornecedorDialog
        open={openDialog}
        onClose={closeDialog}
        onSave={(savedFornecedor) => loadMsg(savedFornecedor)}
        initialData={tempFornecedor}
      />
    </>
  );
}
